import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Car, Paintbrush, Shield, Layers, Loader2 } from "lucide-react";
import SoapyButton from "@/components/landing/SoapyButton";
import { SiteHeading } from "@/lib/siteContent";
import { apiGet } from "@/lib/api";
import { CarBuilder } from "@/components/builder/CarBuilder";

const IMG = {
  interior: "https://static.prod-images.emergentagent.com/jobs/c61668cf-34bc-4a78-b4f3-b1bfcec2b728/images/538b3ec852d97f0605987b2305ae3d2675a392e2c3c9f54ab54f4ac82269e6e5.png",
  exterior: "https://images.pexels.com/photos/28995189/pexels-photo-28995189.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  paint: "https://images.pexels.com/photos/33602226/pexels-photo-33602226.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  full: "https://static.prod-images.emergentagent.com/jobs/c61668cf-34bc-4a78-b4f3-b1bfcec2b728/images/6e6abd3d97186271d263f893a55aed4234710a9fccfa39bcb30f96de219f6c51.png",
};
const look = (s) => {
  const k = `${s.key} ${s.name}`.toLowerCase();
  if (k.includes("interior")) return [IMG.interior, Sparkles];
  if (k.includes("correction") || k.includes("polish")) return [IMG.paint, Paintbrush];
  if (k.includes("ceramic") || k.includes("ppf") || k.includes("film")) return [IMG.full, Shield];
  if (k.includes("wrap") || k.includes("tint")) return [IMG.paint, Layers];
  if (k.includes("full")) return [IMG.full, Shield];
  return [IMG.exterior, Car];
};

const Card = ({ s, i }) => {
  const navigate = useNavigate();
  const [fallback, Icon] = look(s);
  const features = s.features?.length ? s.features : (s.options || []).map((o) => o.label);
  return (
    <motion.div data-testid={`service-card-${s.key}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: (i % 4) * 0.1 }} className="group relative">
      <motion.div className="relative bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-2xl overflow-hidden h-full flex flex-col"
        whileHover={{ y: -10, borderColor: "rgba(14, 165, 233, 0.5)" }} transition={{ duration: 0.3 }}>
        <div className="relative h-40 overflow-hidden">
          <img src={s.image || fallback} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          <div className="absolute top-4 left-4 p-2 rounded-lg bg-sky-500/20 border border-sky-500/30 backdrop-blur-sm"><Icon className="w-5 h-5 text-sky-400" /></div>
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="font-['Outfit'] text-lg text-white">{s.name}</h3>
            <div className="text-right shrink-0">
              <span className="text-sky-500 text-xs tracking-wider uppercase">From</span>
              <p className="text-2xl font-bold text-white" data-testid={`service-price-${s.key}`}>${Math.round(s.base_price)}</p>
            </div>
          </div>
          {s.description && <p className="text-gray-500 text-sm mb-4 line-clamp-2">{s.description}</p>}
          <ul className="space-y-1.5 mb-5 flex-1">
            {features.slice(0, 3).map((f) => (
              <li key={f} className="flex items-center gap-2 text-xs text-gray-400"><Check className="w-3 h-3 text-sky-500 flex-shrink-0" />{f}</li>
            ))}
          </ul>
          <SoapyButton data-testid={`book-${s.key}`} onClick={() => navigate(`/book/${s.key}`)}
            className="w-full bg-white/5 hover:bg-sky-500 border border-white/10 hover:border-sky-500 text-white text-sm">
            Book Now
          </SoapyButton>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  const [list, setList] = useState(null);
  const [build, setBuild] = useState(() => new URLSearchParams(window.location.search).get("build") === "1");
  useEffect(() => { apiGet("/services").then((d) => setList(d.services.filter((s) => !s.addon))).catch(() => setList([])); }, []);
  return (
    <section id="services" data-testid="services-section" className="py-16 sm:py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "linear-gradient(rgba(14, 165, 233, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.3) 1px, transparent 1px)", backgroundSize: "50px 50px" }} />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-sky-500/5 rounded-full blur-[120px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-4 block">Our Services</span>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-4"><SiteHeading k="services.title" /></h1>
          <p className="text-gray-500 max-w-2xl mx-auto">Pick a service, snap one photo of your car and get an instant AI quote.</p>
          <motion.button onClick={() => setBuild(true)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid="open-car-builder"
            className="mt-8 inline-flex items-center gap-3 h-14 px-8 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 text-white font-medium shadow-[0_10px_40px_-10px_rgba(14,165,233,0.8)]">
            <Paintbrush className="w-5 h-5" />Let's Build My Car
          </motion.button>
        </motion.div>
        {!list ? <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto" /> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">{list.map((s, i) => <Card key={s.key} s={s} i={i} />)}</div>
        )}
        <p className="text-center text-gray-600 text-sm mt-10">* Final price is confirmed from your photo, vehicle size and the options you choose.</p>
      </div>
      <CarBuilder open={build} onClose={() => setBuild(false)} />
    </section>
  );
};

export default Services;
